import ReactApexChart from 'react-apexcharts';
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  Tab,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { useState, useMemo } from 'react';
import { formatMoneyUSD } from '../../../utils/functions';
import { GroupedSettlement } from '../../../services/comparative-clientes/comparative-clients.utility';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import useUserStore from '../../../../../common/src/stores/useUserStore';

type SortColumn = 'label' | 'mainPerKg' | 'compPerKg' | 'diffPerKg' | 'diffTotal';

interface Props {
  data: {
    byShips: { [key: string]: GroupedSettlement };
    byWeeks: { [key: string]: GroupedSettlement };
  };
  comparisonType: 'byWeeks' | 'byShips';
  priceType: 'FOB' | 'CIF';
  currencyLabel?: string;
}

const RevenueBreakdownTable: React.FC<Props> = ({
  data,
  comparisonType,
  priceType,
  currencyLabel,
}) => {
  const { t } = useTranslation('settlementsComparative');
  const { darkMode } = useUserStore();
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const [displayMode, setDisplayMode] = useState<'total' | 'perkg'>('total');
  const [sortColumn, setSortColumn] = useState<SortColumn>('diffTotal');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const isLocal = currencyLabel && currencyLabel !== 'USD';

  const fmt = (value: number, decimals = 2) =>
    isLocal
      ? `${value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${currencyLabel}`
      : formatMoneyUSD(value, { decimals });

  const breakdown = useMemo(() => {
    const currentData = data[comparisonType];
    if (!currentData) return null;

    // Main client totals
    let mainRevenue = 0,
      mainCommission = 0,
      mainTax = 0,
      mainOtherCosts = 0,
      mainFreight = 0,
      mainKilos = 0;

    // Comparable totals weighted by main kilos (like-for-like)
    let compRevenueWeighted = 0,
      compCommissionWeighted = 0,
      compTaxWeighted = 0,
      compOtherCostsWeighted = 0,
      compFreightWeighted = 0;

    // Per-detail weighted difference accumulators
    let revenueDiff = 0,
      commissionDiff = 0,
      taxDiff = 0,
      otherCostsDiff = 0,
      freightDiff = 0;

    Object.values(currentData).forEach((group) => {
      Object.entries(group.mainClient.details).forEach(
        ([detailKey, mainDetail]) => {
          let hasComparison = false;
          let detailCompRevenue = 0;
          let detailCompCommission = 0;
          let detailCompTax = 0;
          let detailCompOtherCosts = 0;
          let detailCompFreight = 0;
          let detailCompKilos = 0;

          Object.values(group.comparisons).forEach((client) => {
            const compDetail = client.details[detailKey];
            if (compDetail && compDetail.kilos > 0) {
              hasComparison = true;
              detailCompRevenue += compDetail.totalRevenue;
              detailCompCommission += compDetail.totalCommission;
              detailCompTax += compDetail.totalTax;
              detailCompOtherCosts += compDetail.totalOtherCosts;
              detailCompFreight += compDetail.totalFreight;
              detailCompKilos += compDetail.kilos;
            }
          });

          if (hasComparison && mainDetail.kilos > 0) {
            const mk = mainDetail.kilos;
            const ck = detailCompKilos;

            // Accumulate main client totals
            mainRevenue += mainDetail.totalRevenue;
            mainCommission += mainDetail.totalCommission;
            mainTax += mainDetail.totalTax;
            mainOtherCosts += mainDetail.totalOtherCosts;
            mainFreight += mainDetail.totalFreight;
            mainKilos += mk;

            // Accumulate comparable per-kg values weighted by main kilos
            // This ensures same product mix as principal for fair comparison
            compRevenueWeighted += (detailCompRevenue / ck) * mk;
            compCommissionWeighted += (detailCompCommission / ck) * mk;
            compTaxWeighted += (detailCompTax / ck) * mk;
            compOtherCostsWeighted += (detailCompOtherCosts / ck) * mk;
            compFreightWeighted += (detailCompFreight / ck) * mk;

            // Compute per-detail per-kg difference, weighted by main kilos
            revenueDiff +=
              (mainDetail.totalRevenue / mk - detailCompRevenue / ck) * mk;
            commissionDiff +=
              (mainDetail.totalCommission / mk - detailCompCommission / ck) *
              mk;
            taxDiff += (mainDetail.totalTax / mk - detailCompTax / ck) * mk;
            otherCostsDiff +=
              (mainDetail.totalOtherCosts / mk - detailCompOtherCosts / ck) *
              mk;
            freightDiff +=
              (mainDetail.totalFreight / mk - detailCompFreight / ck) * mk;
          }
        }
      );
    });

    if (mainKilos === 0) return null;

    // Per-kg display values
    const mainRevenuePerKg = mainRevenue / mainKilos;
    const mainCommissionPerKg = mainCommission / mainKilos;
    const mainTaxPerKg = mainTax / mainKilos;
    const mainOtherCostsPerKg = mainOtherCosts / mainKilos;
    const mainFreightPerKg = mainFreight / mainKilos;

    // Comparable per-kg weighted by main kilos (like-for-like comparison)
    const compRevenuePerKg = compRevenueWeighted / mainKilos;
    const compCommissionPerKg = compCommissionWeighted / mainKilos;
    const compTaxPerKg = compTaxWeighted / mainKilos;
    const compOtherCostsPerKg = compOtherCostsWeighted / mainKilos;
    const compFreightPerKg = compFreightWeighted / mainKilos;

    const mainNetPerKg =
      mainRevenuePerKg -
      mainCommissionPerKg -
      mainTaxPerKg -
      mainOtherCostsPerKg -
      mainFreightPerKg;
    const compNetPerKg =
      compRevenuePerKg -
      compCommissionPerKg -
      compTaxPerKg -
      compOtherCostsPerKg -
      compFreightPerKg;

    // Net difference = sum of component differences (revenue positive, costs subtracted)
    const netDiff =
      revenueDiff - commissionDiff - taxDiff - otherCostsDiff - freightDiff;

    const costRow = (
      label: string,
      mainVal: number,
      compVal: number,
      diffTotal: number
    ) => ({
      label,
      mainPerKg: -mainVal,
      compPerKg: -compVal,
      diffPerKg: -diffTotal / mainKilos,
      diffTotal: -diffTotal,
    });

    return {
      rows: [
        {
          label: t('Ingresos'),
          mainPerKg: mainRevenuePerKg,
          compPerKg: compRevenuePerKg,
          diffPerKg: revenueDiff / mainKilos,
          diffTotal: revenueDiff,
        },
        costRow(
          t('Comisión'),
          mainCommissionPerKg,
          compCommissionPerKg,
          commissionDiff
        ),
        costRow(t('Impuestos'), mainTaxPerKg, compTaxPerKg, taxDiff),
        costRow(
          t('Otros Costos'),
          mainOtherCostsPerKg,
          compOtherCostsPerKg,
          otherCostsDiff
        ),
        ...(priceType === 'FOB'
          ? [
              costRow(
                t('Flete'),
                mainFreightPerKg,
                compFreightPerKg,
                freightDiff
              ),
            ]
          : []),
      ],
      net: {
        label: t('Neto'),
        mainPerKg: mainNetPerKg,
        compPerKg: compNetPerKg,
        diffPerKg: netDiff / mainKilos,
        diffTotal: netDiff,
      },
      mainKilos,
    };
  }, [data, comparisonType, priceType, t]);

  const chartOptions = useMemo(() => {
    if (!breakdown) return {};
    const allItems = [...breakdown.rows, breakdown.net];
    const categories = allItems.map((r) => r.label);

    return {
      chart: {
        type: 'bar' as const,
        height: 350,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
          colors: {
            ranges: [
              { from: -Infinity, to: 0, color: '#f87171' },
              { from: 0, to: Infinity, color: '#4ade80' },
            ],
          },
        },
      },
      grid: {
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      annotations: {
        yaxis: [
          {
            y: 0,
            strokeDashArray: 0,
            borderColor: '#FF0000',
            borderWidth: 2,
            opacity: 0.8,
            width: '100%',
            label: {
              borderColor: '#FF0000',
              style: { color: '#fff', background: '#FF0000' },
              text: '0',
            },
          },
        ],
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => {
          if (displayMode === 'perkg') {
            return isLocal
              ? `${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyLabel}`
              : formatMoneyUSD(val, { decimals: 2 });
          }
          return isLocal
            ? `${Math.abs(Number(val)).toLocaleString('es-CL', { maximumFractionDigits: 0 })} ${currencyLabel}`
            : `$${Math.abs(Number(val)).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
        },
        style: {
          colors: [darkMode ? '#fff' : '#000'],
        },
      },
      yaxis: {
        title: {
          text:
            displayMode === 'perkg'
              ? `${t('Diferencia')} /kg`
              : isLocal
                ? `${t('Diferencia Total')} (${currencyLabel})`
                : t('Diferencia Total (USD)'),
        },
        labels: {
          formatter: (val: number) => {
            if (displayMode === 'perkg') {
              return isLocal
                ? `${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyLabel}`
                : formatMoneyUSD(val, { decimals: 2 });
            }
            return isLocal
              ? `${val.toLocaleString('es-CL', { maximumFractionDigits: 0 })} ${currencyLabel}`
              : `$${val.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
          },
        },
      },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          style: { fontSize: '12px' },
        },
      },
    };
  }, [breakdown, displayMode, darkMode, currencyLabel, t]);

  const chartSeries = useMemo(() => {
    if (!breakdown) return [];
    const allItems = [...breakdown.rows, breakdown.net];
    return [
      {
        name:
          displayMode === 'perkg' ? t('Diferencia /kg') : t('Diferencia Total'),
        data: allItems.map((r) =>
          displayMode === 'perkg' ? r.diffPerKg : r.diffTotal
        ),
      },
    ];
  }, [breakdown, displayMode, t]);

  if (!breakdown) return null;

  const diffColor = (value: number) =>
    value > 0 ? 'text-success-500' : value < 0 ? 'text-danger-500' : '';

  return (
    <Card className='w-full md:p-4'>
      <CardHeader>
        <div className='flex flex-col sm:flex-row justify-between w-full items-center gap-4'>
          <h3 className='text-lg font-bold text-center sm:text-left'>
            {t('Diferencia por Ingresos/Costos')}
          </h3>
          <div className='flex gap-4 w-full sm:w-auto items-center'>
            <Tabs
              size='sm'
              selectedKey={viewMode}
              onSelectionChange={(key) => setViewMode(key as 'chart' | 'table')}
            >
              <Tab key='chart' title={t('Gráfico')} />
              <Tab key='table' title={t('Tabla')} />
            </Tabs>
            {viewMode === 'chart' && (
              <Select
                label={t('Tipo de Valor')}
                value={displayMode}
                defaultSelectedKeys={['total']}
                onChange={(e) =>
                  setDisplayMode(e.target.value as 'total' | 'perkg')
                }
                size='sm'
                className='w-full sm:min-w-40'
              >
                <SelectItem key='total' value='total'>
                  {isLocal ? `${t('Valor')} ${currencyLabel}` : t('Valor USD')}
                </SelectItem>
                <SelectItem key='perkg' value='perkg'>
                  {t('Diferencia')} /kg
                </SelectItem>
              </Select>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {viewMode === 'chart' ? (
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type='bar'
            height={350}
          />
        ) : (
          <Table removeWrapper aria-label='Revenue breakdown table' sortDescriptor={{ column: sortColumn, direction: sortDirection === 'asc' ? 'ascending' : 'descending' }} onSortChange={(desc) => { setSortColumn(desc.column as SortColumn); setSortDirection(desc.direction === 'ascending' ? 'asc' : 'desc'); }}>
            <TableHeader>
              <TableColumn key='label' allowsSorting>{t('Componente')}</TableColumn>
              <TableColumn key='mainPerKg' allowsSorting align='end'>{t('Principal')} /kg</TableColumn>
              <TableColumn key='compPerKg' allowsSorting align='end'>{t('Comparable')} /kg</TableColumn>
              <TableColumn key='diffPerKg' allowsSorting align='end'>{t('Diferencia')} /kg</TableColumn>
              <TableColumn key='diffTotal' allowsSorting align='end'>{t('Diferencia Total')}</TableColumn>
            </TableHeader>
            <TableBody>
              {[
                ...[...breakdown.rows].sort((a, b) => {
                  const aVal = a[sortColumn];
                  const bVal = b[sortColumn];
                  if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                  }
                  return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
                }).map((row) => (
                  <TableRow key={row.label}>
                    <TableCell className='font-medium'>{row.label}</TableCell>
                    <TableCell className='text-right'>
                      {fmt(row.mainPerKg)}
                    </TableCell>
                    <TableCell className='text-right'>
                      {fmt(row.compPerKg)}
                    </TableCell>
                    <TableCell
                      className={`text-right font-medium ${diffColor(row.diffPerKg)}`}
                    >
                      {row.diffPerKg > 0 ? '+' : ''}
                      {fmt(row.diffPerKg)}
                    </TableCell>
                    <TableCell
                      className={`text-right font-medium ${diffColor(row.diffTotal)}`}
                    >
                      {row.diffTotal > 0 ? '+' : ''}
                      {fmt(row.diffTotal, 0)}
                    </TableCell>
                  </TableRow>
                )),
                <TableRow
                  key='net'
                  className='bg-default-50 dark:bg-default-100'
                >
                  <TableCell className='font-bold'>
                    {breakdown.net.label}
                  </TableCell>
                  <TableCell className='text-right font-bold'>
                    {fmt(breakdown.net.mainPerKg)}
                  </TableCell>
                  <TableCell className='text-right font-bold'>
                    {fmt(breakdown.net.compPerKg)}
                  </TableCell>
                  <TableCell
                    className={`text-right font-bold ${diffColor(breakdown.net.diffPerKg)}`}
                  >
                    {breakdown.net.diffPerKg > 0 ? '+' : ''}
                    {fmt(breakdown.net.diffPerKg)}
                  </TableCell>
                  <TableCell
                    className={`text-right font-bold ${diffColor(breakdown.net.diffTotal)}`}
                  >
                    {breakdown.net.diffTotal > 0 ? '+' : ''}
                    {fmt(breakdown.net.diffTotal, 0)}
                  </TableCell>
                </TableRow>,
              ]}
            </TableBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

export default RevenueBreakdownTable;
